import { ArrowBack as BackIcon, Info as InfoIcon } from '@mui/icons-material';
import { Alert, Box, Button, Typography } from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { getDocumentStatus } from '../services/api';
import { updateFactCheckItem, updateItemFacts } from '../services/factCheckItemService';
import { IMG } from '../theme/assets';
import type { DocumentStatusResponse } from '../types';

// ポーリング間隔（ミリ秒）
const POLL_INTERVAL_MS = 2500;

const STAGES = [
  { key: 'ocr', label: 'OCR読み取り', icon: IMG.create.pdf, desc: 'スキャン画像から文字を抽出' },
  {
    key: 'extracting',
    label: 'ファクト抽出',
    icon: IMG.process.extract,
    desc: '読み取ったテキストから主張を抽出',
  },
  { key: 'completed', label: '完了', icon: IMG.process.complete, desc: '検証する主張の準備が完了' },
];

const DocumentProcessing: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const tokens = useThemeColors();

  // 一覧から遷移した場合は location.state が無いが、処理キー(sessionId)= item.id のため
  // それを itemId のフォールバックに使う（完了時の /workflow 遷移・アイテム更新が成立する）。
  const itemId = ((location.state as any)?.itemId as string | undefined) ?? sessionId;
  const workspaceId = (location.state as any)?.workspaceId || null;
  const itemTitle = (location.state as any)?.itemTitle as string | undefined;

  const [status, setStatus] = useState<DocumentStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [noFactsFound, setNoFactsFound] = useState(false);
  const finishedRef = useRef(false);

  const handleGoBack = useCallback(() => {
    if (workspaceId) {
      navigate(`/share/${workspaceId}`);
    } else {
      navigate('/');
    }
  }, [navigate, workspaceId]);

  // 完了時の処理（ファクトをアイテムに保存して確認画面へ）
  const handleCompleted = useCallback(
    async (data: DocumentStatusResponse) => {
      if (finishedRef.current) return;
      finishedRef.current = true;

      try {
        if (itemId) {
          await updateItemFacts(itemId, data.facts);
          await updateFactCheckItem(itemId, {
            status: 'extracted',
            processingStage: null,
            facts: data.facts as any,
            total_facts: data.total_count,
            verified_facts: 0,
          });
        }
      } catch (err) {
        console.error('❌ ファクト保存エラー:', err);
      }

      if (data.total_count === 0) {
        setNoFactsFound(true);
      } else if (itemId) {
        navigate(`/workflow/${itemId}`, {
          state: {
            extractedFacts: data.facts,
            pdfUrl: (location.state as any)?.pdfUrl,
            filename: (location.state as any)?.filename,
            itemTitle,
            itemDescription: (location.state as any)?.itemDescription,
          },
        });
      } else {
        handleGoBack();
      }
    },
    [itemId, navigate, location.state, itemTitle, handleGoBack],
  );

  // ステータスポーリング
  useEffect(() => {
    if (!sessionId || !user) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const poll = async () => {
      const res = await getDocumentStatus(sessionId);
      if (cancelled) return;

      if (!res.success || !res.data) {
        setError(res.error || 'ステータスの取得に失敗しました。');
        return;
      }

      setStatus(res.data);

      if (res.data.status === 'error' || res.data.stage === 'error') {
        setError(res.data.error || 'OCR処理でエラーが発生しました。');
        return;
      }

      if (res.data.status === 'completed') {
        await handleCompleted(res.data);
        return;
      }

      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, user, handleCompleted]);

  const stage = status?.stage;
  const isError = Boolean(error) || stage === 'error';
  const isCompleted = stage === 'completed' || status?.status === 'completed';
  const progress = Math.round(status?.progress ?? 0);

  let cur = 0;
  if (stage === 'extracting') cur = 1;
  else if (isCompleted) cur = 2;

  const stageStatus = (i: number): 'done' | 'active' | 'error' | 'pending' => {
    if (isError) return i < cur ? 'done' : i === cur ? 'error' : 'pending';
    if (isCompleted) return 'done';
    return i < cur ? 'done' : i === cur ? 'active' : 'pending';
  };

  const heroTitle = isError
    ? '処理エラー'
    : isCompleted
      ? '処理完了！'
      : stage === 'extracting'
        ? 'ファクト抽出中…'
        : 'OCR読み取り中…';
  const heroSub = isError
    ? (error ?? 'エラーが発生しました。再試行してください。')
    : isCompleted
      ? '検証する主張の確認に進めます。'
      : (status?.message ?? 'スキャンされたPDFを解析しています。');

  const barColor = isError
    ? 'linear-gradient(90deg, #dc2626, #f87171)'
    : isCompleted
      ? 'linear-gradient(90deg, #16a34a, #34d399)'
      : `linear-gradient(90deg, ${tokens.teal}, #34d399)`;
  const pctColor = isError ? '#dc2626' : isCompleted ? '#16a34a' : tokens.teal;

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', py: { xs: 2, md: 4 } }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 2.5 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleGoBack}
          sx={{
            background: tokens.paper,
            border: `1px solid ${tokens.line}`,
            borderRadius: '11px',
            px: 1.75,
            py: 0.9,
            fontSize: 13,
            fontWeight: 700,
            color: tokens.ink,
            textTransform: 'none',
            '&:hover': { background: tokens.paper, borderColor: tokens.teal },
          }}
        >
          {workspaceId ? '案件に戻る' : 'マイページに戻る'}
        </Button>
        <Typography sx={{ fontSize: 20, fontWeight: 900, color: 'text.primary' }}>
          ドキュメントを処理中:{' '}
          <Box component="span" sx={{ color: tokens.teal }}>
            {itemTitle || '...'}
          </Box>
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {noFactsFound ? (
          <Box
            sx={{
              textAlign: 'center',
              background: `linear-gradient(135deg, ${tokens.heroFrom}, ${tokens.heroTo})`,
              border: `1px solid ${tokens.line}`,
              borderRadius: '16px',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              component="img"
              src={IMG.process.complete}
              alt=""
              sx={{ width: 88, height: 88, objectFit: 'contain', mb: 1 }}
            />
            <Typography sx={{ fontSize: 20, fontWeight: 900, color: 'text.primary' }}>
              検証可能なファクトが見つかりませんでした
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1, lineHeight: 1.6 }}>
              この資料には、客観的に検証可能な主張が含まれていませんでした。
            </Typography>
            <Alert severity="info" sx={{ textAlign: 'left', mt: 2.5, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                対象となる例
              </Typography>
              <Typography variant="body2" component="div">
                ✅ 統計・数値 / 歴史的事実 / 公式発表 / 人物プロフィール
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}>
                対象外の例
              </Typography>
              <Typography variant="body2" component="div">
                ❌ 個人的な意見・感想 / 主観的評価 / 未来予測 / 相談内容
              </Typography>
            </Alert>
            <Button
              onClick={handleGoBack}
              sx={{
                mt: 2.5,
                background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
                color: '#fff',
                fontWeight: 800,
                borderRadius: '12px',
                px: 3,
                py: 1.1,
                textTransform: 'none',
              }}
            >
              一覧に戻る
            </Button>
          </Box>
        ) : (
          <>
            {/* 進捗ヒーロー */}
            <Box
              sx={{
                textAlign: 'center',
                background: isError
                  ? 'linear-gradient(135deg, #fdeaea, #fff3f3)'
                  : `linear-gradient(135deg, ${tokens.heroFrom}, ${tokens.heroTo})`,
                border: `1px solid ${tokens.line}`,
                borderRadius: '16px',
                p: { xs: 3, md: 3.5 },
              }}
            >
              <Box
                component="img"
                src={isCompleted ? IMG.process.complete : IMG.owl.clipboard}
                alt=""
                sx={{
                  width: 92,
                  height: 92,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 8px 16px rgba(30,30,70,0.18))',
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.25,
                  mt: 1,
                }}
              >
                {!isError && !isCompleted && (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      border: `3px solid ${tokens.teal}40`,
                      borderTopColor: tokens.teal,
                      borderRadius: '50%',
                      animation: 'dpspin 0.9s linear infinite',
                      '@keyframes dpspin': { to: { transform: 'rotate(360deg)' } },
                    }}
                  />
                )}
                <Typography sx={{ fontSize: 21, fontWeight: 900, color: 'text.primary' }}>
                  {heroTitle}
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75, lineHeight: 1.55 }}
              >
                {heroSub}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2.5 }}>
                <Box
                  sx={{
                    flex: 1,
                    height: 13,
                    borderRadius: '8px',
                    background: '#dfe4ee',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${progress}%`,
                      background: barColor,
                      borderRadius: '8px',
                      transition: 'width 0.5s ease',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 900,
                    color: pctColor,
                    minWidth: 48,
                    textAlign: 'right',
                  }}
                >
                  {progress}%
                </Typography>
              </Box>
              {status?.page_count ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mt: 2,
                    background: '#fff',
                    border: `1px solid ${tokens.line}`,
                    borderRadius: 999,
                    px: 1.75,
                    py: 0.75,
                    fontSize: 12,
                    fontWeight: 800,
                    color: 'text.secondary',
                  }}
                >
                  {status.page_count}ページ
                </Box>
              ) : null}
            </Box>

            {/* ステージトラッカー */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: `1px solid ${tokens.line}`,
                borderRadius: '13px',
                px: 2.5,
                py: 0.5,
              }}
            >
              {STAGES.map((g, i) => {
                const st = stageStatus(i);
                return (
                  <Box
                    key={g.key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.75,
                      py: 1.75,
                      borderBottom: i < STAGES.length - 1 ? `1px solid ${tokens.line}` : 'none',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: 50,
                        height: 50,
                        borderRadius: '14px',
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        background:
                          st === 'done'
                            ? '#e7f6ed'
                            : st === 'active'
                              ? '#eafaf6'
                              : st === 'error'
                                ? '#fdeaea'
                                : '#f1f3f7',
                        boxShadow:
                          st === 'active'
                            ? `0 0 0 3px ${tokens.teal}2e`
                            : st === 'error'
                              ? '0 0 0 3px rgba(220,38,38,0.16)'
                              : 'none',
                      }}
                    >
                      <Box
                        component="img"
                        src={g.icon}
                        alt=""
                        sx={{
                          width: 34,
                          height: 34,
                          objectFit: 'contain',
                          filter: st === 'pending' ? 'grayscale(1)' : 'none',
                          opacity: st === 'pending' ? 0.45 : 1,
                        }}
                      />
                      {st === 'done' && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: -5,
                            bottom: -5,
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            background: '#16a34a',
                            border: '2px solid #fff',
                            color: '#fff',
                            fontSize: 13,
                            fontWeight: 900,
                            display: 'grid',
                            placeItems: 'center',
                          }}
                        >
                          ✓
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 14.5,
                            fontWeight: 900,
                            color: st === 'pending' ? tokens.faint : 'text.primary',
                          }}
                        >
                          {g.label}
                        </Typography>
                        {st === 'active' ? (
                          <Box
                            sx={{
                              width: 15,
                              height: 15,
                              border: `2.5px solid ${tokens.teal}40`,
                              borderTopColor: tokens.teal,
                              borderRadius: '50%',
                              animation: 'dpspin 0.9s linear infinite',
                            }}
                          />
                        ) : (
                          <Box
                            component="span"
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 900,
                              px: 1,
                              py: '2px',
                              borderRadius: 999,
                              background:
                                st === 'done' ? '#e7f6ed' : st === 'error' ? '#fdeaea' : '#eef1f5',
                              color:
                                st === 'done'
                                  ? '#16a34a'
                                  : st === 'error'
                                    ? '#dc2626'
                                    : tokens.faint,
                            }}
                          >
                            {st === 'done' ? '完了' : st === 'error' ? 'エラー' : '待機中'}
                          </Box>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
                        {g.desc}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* 案内ノート */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.25,
                alignItems: 'flex-start',
                background: 'rgba(102,126,234,0.10)',
                border: `1px solid ${tokens.line}`,
                borderRadius: '14px',
                p: 2,
              }}
            >
              <InfoIcon sx={{ fontSize: 18, color: tokens.indigo, mt: '1px' }} />
              <Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: 'text.primary' }}>
                  このまま画面を離れても大丈夫です。
                </Typography>
                <Typography
                  sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25, lineHeight: 1.55 }}
                >
                  処理はバックグラウンドで継続されます。完了後、一覧画面から詳細を確認できます。
                </Typography>
              </Box>
            </Box>

            {/* エラー時の戻るCTA */}
            {isError && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
                <Button
                  onClick={handleGoBack}
                  sx={{
                    background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
                    color: '#fff',
                    fontWeight: 800,
                    borderRadius: '12px',
                    px: 2.5,
                    py: 1.1,
                    textTransform: 'none',
                  }}
                >
                  一覧に戻る
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default DocumentProcessing;
